import React, { useState } from 'react';
import { Mail, Phone, User, MessageSquare, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import Footer from './Footer';
import Header from './Header';
import { useDispatch } from 'react-redux';
import { createRecord } from '../actions/createRecordSlice';
import '../css/ContactPage.css';

const ContactPage = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear status when user starts typing
    if (submitStatus) {
      setSubmitStatus(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const action = await dispatch(createRecord({ formData, token: 'testtoken' }));
      if (action.type === 'contact/createRecord/fulfilled') {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
        });
        // Clear success message after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page-wrapper">
      <Header />
      <main className="contact-main-content">
      {/* Hero Section */}
      <section className="contact-hero-section">
        <div className="contact-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="contact-hero-text"
          >
            <h1 className="contact-hero-title">Contact Us</h1>
            <p className="contact-hero-subtitle">
              We're here to help! Have questions about our test preparation resources, need assistance with your account, or want to provide feedback? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="contact-container">
        {/* Contact Information Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="contact-info-section"
        >
          <h2 className="contact-section-title">Get in Touch</h2>
          <p className="contact-section-intro">
            Choose the method that works best for you. We typically respond within 24-48 hours.
          </p>
          
          <div className="contact-methods">
            <div className="contact-method-card">
              <div className="contact-method-icon">
                <Mail size={32} />
              </div>
              <h3 className="contact-method-title">Email Us</h3>
              <p className="contact-method-text">
                Send us an email and we'll get back to you as soon as possible.
              </p>
              <a href="mailto:smartkidsprep25@gmail.com" className="contact-method-link">
                smartkidsprep25@gmail.com
              </a>
            </div>

            <div className="contact-method-card">
              <div className="contact-method-icon">
                <MessageSquare size={32} />
              </div>
              <h3 className="contact-method-title">Contact Form</h3>
              <p className="contact-method-text">
                Fill out the form below and we'll respond to your inquiry promptly.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Contact Form Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="contact-form-section"
        >
          <h2 className="contact-section-title">Send Us a Message</h2>
          <p className="contact-form-intro">
            Fill out the form below and we'll get back to you as soon as possible. All fields marked with * are required.
          </p>

          <form onSubmit={handleSubmit} className="contact-form" noValidate>
            {/* Name Field */}
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                <User size={18} className="form-icon" />
                Your Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
                required
                className="form-input"
                aria-required="true"
              />
            </div>

            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                <Mail size={18} className="form-icon" />
                Your Email <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={handleChange}
                required
                className="form-input"
                aria-required="true"
              />
            </div>

            {/* Phone Field */}
            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                <Phone size={18} className="form-icon" />
                Your Phone (Optional)
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder="Enter your phone number"
                value={formData.phone}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            {/* Message Field */}
            <div className="form-group">
              <label htmlFor="message" className="form-label">
                <MessageSquare size={18} className="form-icon" />
                Your Message <span className="required">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                placeholder="Tell us how we can help you..."
                value={formData.message}
                onChange={handleChange}
                required
                rows="6"
                className="form-textarea"
                aria-required="true"
              />
            </div>

            {/* Submit Status Messages */}
            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="form-status form-status-success"
              >
                <CheckCircle size={20} />
                <span>Your message has been sent successfully! We'll get back to you soon.</span>
              </motion.div>
            )}

            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="form-status form-status-error"
              >
                <AlertCircle size={20} />
                <span>Something went wrong. Please try again later or email us directly.</span>
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              className="contact-submit-button"
            >
              {isSubmitting ? (
                <>
                  <span className="button-spinner"></span>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Send Message
                </>
              )}
            </motion.button>
          </form>
        </motion.section>

        {/* Additional Information Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="contact-additional-info"
        >
          <h2 className="contact-section-title">Frequently Asked Questions</h2>
          <p className="contact-additional-text">
            Before contacting us, you might find answers to common questions in our <a href="/about">About page</a> or by checking the FAQ sections on our test preparation pages for <a href="/cogat">CoGAT</a>, <a href="/nnat">NNAT</a>, and <a href="/iaat">IAAT</a>.
          </p>
          <p className="contact-additional-text">
            For account-related issues, please include your email address in your message so we can assist you more effectively.
          </p>
        </motion.section>
      </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
